import { useCallback, useEffect, useRef, useState } from "react";
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  Alert,
  Badge,
} from "react-bootstrap";
import { formatText, getRoleById } from "helpers/common";
import { ROLE_TYPES } from "common/constants";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import update from 'immutability-helper';
import { Prompt } from "react-router-dom";
import { useDispatch } from "react-redux";
import { applySorting } from "store/services/emailPreferenceService";
import * as actions from "store/actions";

const EmailPreferenceModule = ({
  data,
  handleEdit,
  handleEditPreference,
}) => {
  const dispatch = useDispatch();
  const [modules, setModules] = useState(null);
  const [unsavedCurrentSorting, setUnsavedCurrentSorting] = useState(null);

  useEffect(() => {
    setModules(data);
  }, [data]);

  const moveCard = useCallback((dragIndex, hoverIndex) => {
    setModules((prev) => {
      let sorted = update(prev, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, prev[dragIndex]],
        ],
      });

      let newSorting = sorted.map((item, i) => ({
        id: item.module_id, 
        sort_order: i
      }))

      setUnsavedCurrentSorting(newSorting);

      return sorted;
    });
  }, [])

  const saveSorting = async () => {
    try {
      const payload = {
        type: "module",
        orders: unsavedCurrentSorting
      }
      dispatch(actions.persist_store({ loader: true }));
      await dispatch(applySorting(payload));
      setUnsavedCurrentSorting(null);
    } catch(err) {
      console.error("Error saving sorting", err);
    } finally {
      dispatch(actions.persist_store({ loader: false }));
    }
  }

  return (
    <div>
      {unsavedCurrentSorting && (
        <div className="py-2">
          <Prompt
            when={() => !!unsavedCurrentSorting}
            message='You have unsaved changes, are you sure you want to leave?'
          />
          <div className="d-flex gap-10 align-items-center text-end justify-content-end">
            <p className="fw-normal m-0">Order of the modules has been changed, do you want to save the changes?</p>
            <Button
              className="btn"
              variant="danger"
              onClick={() => {
                setUnsavedCurrentSorting(null);
                window.location.reload();
              }}
            >
              Discard changes
            </Button>
            <Button
              className="btn btn-primary"
              onClick={saveSorting}
            >
              Yes
            </Button>
          </div>
        </div>
      )}
      <DndProvider backend={HTML5Backend}>
        {modules?.length ?
          modules.map((module, modIndex) => (
            <Module
              key={modIndex}
              index={modIndex}
              module={module}
              handleEdit={handleEdit}
              handleEditPreference={handleEditPreference}
              moveCard={moveCard}
            />
          )) : (
            <p>No data found</p>
          )}
      </DndProvider>
    </div>
  );
};

const Module = ({ index, module, handleEdit, handleEditPreference, moveCard }) => {

  const ref = useRef(null);

  const [{ handlerId }, drop] = useDrop({
    accept: `module`,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(draggedItem, monitor) {
      if (!ref.current) {
        return;
      }

      const dragIndex = draggedItem.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveCard(dragIndex, hoverIndex);
      draggedItem.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: `module`,
    item: () => ({ index }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  const style = {
    cursor: 'move',
    opacity: isDragging ? 0.4 : 1,
    border: isDragging ? "1px dashed gray" : "none",
    borderBottom: "1px solid #ccc",
  };

return (
  <div ref={ref} style={style} data-handler-id={handlerId} className="mb-3">
    <div
      className="mb-4"
    >
      <Card>
        <Card.Body>
          <Card.Title>
            {module.module_name}
            <Button
              size="sm"
              variant="transparent"
              onClick={() => handleEdit(module)}
            >
              <i className="fa fa-lg fa-edit"></i>
            </Button>
            <Badge>{formatText(module.status)}</Badge>
          </Card.Title>
          <Card.Subtitle className="mb-3 text-muted">
            {module.description}
          </Card.Subtitle>

          {module.preferences?.length > 0 ? (
            <Form>
              <Row>
                <Col md={6}>
                  <RenderPerferences
                    module={module}
                    userType={ROLE_TYPES.practitioner}
                    handleEdit={handleEditPreference}
                  />
                </Col>
                <Col md={6}>
                  <RenderPerferences
                    module={module}
                    userType={ROLE_TYPES.client}
                    handleEdit={handleEditPreference}
                  />
                </Col>
              </Row>
              <Row>
                <Col md="12">
                  <AddPreferenceButton
                    handleClick={handleEditPreference}
                    module={module}
                  />
                </Col>
              </Row>
            </Form>
          ) : (
            <Alert variant="secondary" className="position-relative">
              No preferences available.
              <AddPreferenceButton
                className="justify-content-end position-absolute end-0 top-0 mt-2 me-2"
                handleClick={handleEditPreference}
                module={module}
              />
            </Alert>
          )}
        </Card.Body>
      </Card>
    </div>
  </div>
)
}

const RenderPerferences = ({ module, userType, handleEdit }) => {
  const dispatch = useDispatch();
  const [preferences, setPreferences] = useState(null);
  const [unsavedCurrentSorting, setUnsavedCurrentSorting] = useState(null);

  useEffect(() => {
    const filtered = module?.preferences?.length && module?.preferences.filter((p) => p.user_type === userType);
    setPreferences(filtered);
  }, [module, userType]);

  const moveCard = useCallback((dragIndex, hoverIndex) => {
    setPreferences((prev) => {
      let sorted = update(prev, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, prev[dragIndex]],
        ],
      });

      let newSorting = sorted.map((item, i) => ({
        id: item.id, 
        sort_order: i
      }))

      setUnsavedCurrentSorting(newSorting);

      return sorted;
    });
  }, [])

  const saveSorting = async () => {
    try {
      const payload = {
        type: "preference",
        orders: unsavedCurrentSorting
      }
      dispatch(actions.persist_store({ loader: true }));
      await dispatch(applySorting(payload));
      setUnsavedCurrentSorting(null);
    } catch(err) {
      console.error("Error saving sorting", err);
    } finally {
      dispatch(actions.persist_store({ loader: false }));
    }
  }

  return (
    <div>
      <h6>{formatText(getRoleById(userType))} panel</h6>
      <div className="border border-1 rounded-sm border-light p-2 shadow mb-3">
        <DndProvider backend={HTML5Backend}>
          {preferences?.length ? (
            preferences.map((pref, pIndex) => (
              <Preference
                moduleId={module.module_id}
                userType={userType}
                index={pIndex}
                pref={pref}
                handleEdit={handleEdit}
                moveCard={moveCard}
              />
            ))
          ) : (
            <div className="mb-3 ps-4 border p-2 rounded bg-light text-center">
              <p>No preferences found</p>
            </div>
          )}
        </DndProvider>
        {unsavedCurrentSorting && (
          <div className="py-2">
            <Prompt
              when={() => !!unsavedCurrentSorting}
              message='You have unsaved changes, are you sure you want to leave?'
            />
            <div className="">
              <p className="fw-normal m-0">Order of the preferences has been changed, do you want to save the changes?</p>
              <div className="d-flex gap-10 align-items-center justify-content-center">
                <Button
                  className="btn"
                  variant="danger"
                  onClick={() => {
                    setUnsavedCurrentSorting(null);
                    window.location.reload();
                  }}
                >
                  Discard changes
                </Button>
                <Button
                  className="btn btn-primary"
                  onClick={saveSorting}
                >
                  Yes
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const Preference = ({ index, moduleId, userType, handleEdit, pref, moveCard }) => {
  const ref = useRef(null);

  const [{ handlerId }, drop] = useDrop({
    accept: `preference_item_${moduleId}_${userType}`,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(draggedItem, monitor) {
      if (!ref.current) {
        return;
      }

      const dragIndex = draggedItem.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveCard(dragIndex, hoverIndex);
      draggedItem.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: `preference_item_${moduleId}_${userType}`,
    item: () => ({ index }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  const style = {
    cursor: 'move',
    opacity: isDragging ? 0.4 : 1,
    border: isDragging ? "1px dashed gray" : "none",
    borderBottom: "1px solid #ccc",
  };

  return (
    <div ref={ref} style={style} data-handler-id={handlerId} className="mb-3">
      <div
        className="ps-4 border p-2 rounded bg-light position-relative"
      >
        <strong>{pref.name}</strong>
        <Button
          size="sm"
          variant="transparent"
          onClick={() =>
            handleEdit(pref)
          }
          style={{
            position: "absolute",
            top: 2,
            right: 2
          }}
        >
          <i className="fa fa-lg fa-edit"></i>
        </Button>
        <br />
        <small className="text-muted">
          {pref.description}
        </small>
      </div>
    </div>
  )
}

const AddPreferenceButton = ({
  module,
  handleClick,
  className = "d-flex justify-content-end",
}) => {
  return (
    <div className={className}>
      <Button
        onClick={() => {
          handleClick({
            module_id: module?.module_id,
          });
        }}
      >
        Add Preference
      </Button>
    </div>
  );
};

export default EmailPreferenceModule;
